import { Alert, AlertTitle, Stack, Step, StepLabel, Stepper } from '@mui/material';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Page } from '../../../shared';
import { SupportCaseTitle } from '../../components';
import { useGetIntake, useGetSupportCase } from '../../hooks';
import { IntakeCompletionPage } from './intake-completion/intake-completion.page';
import { IntakeExpensesPage } from './intake-expense/intake-expense.page';
import { IntakeIncomePage } from './intake-income/intake-income.page';
import { IntakeIntroPage } from './intake-intro/intake-intro.page';

interface RouteProps {
    id: string;
    intakeId: string;
}
export const SupportCaseIntakeWizardPage: FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { id: supportCaseId, intakeId } = useParams<keyof RouteProps>();
    const { data: supportCase } = useGetSupportCase(supportCaseId as string);

    const { data: intake } = useGetIntake(supportCaseId, intakeId);

    const steps = useMemo(() => ['intro', 'income', 'expenses', 'completion'], []);
    const activeStep = useMemo(() => {
        const activeStepPath = location.pathname.split('/').pop();
        return Math.max(steps.indexOf(`${activeStepPath}`), 0);
    }, [location, steps]);

    return (
        <Page
            title={
                supportCase && (
                    <Stack direction="row" alignItems="center">
                        {t('newIntake')}: <SupportCaseTitle supportCase={supportCase} />
                    </Stack>
                )
            }
        >
            <Stack spacing={2} py={2}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((step) => (
                        <Step key={step}>
                            <StepLabel>{t('intakeStep.' + step)}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {intake?.externalIntakeError && (
                    <Alert severity={'error'}>
                        <AlertTitle mb={0}>{t('externalIntakeError')}</AlertTitle>
                    </Alert>
                )}
            </Stack>

            <Routes>
                <Route path="/intro" element={<IntakeIntroPage intake={intake} supportCaseId={supportCaseId} />} />
                <Route path="/income" element={<IntakeIncomePage intake={intake} />} />
                <Route path="/expenses" element={<IntakeExpensesPage intake={intake} />} />
                <Route path="/completion" element={<IntakeCompletionPage intake={intake} />} />
            </Routes>
        </Page>
    );
};
